import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate, useLocation } from 'react-router-dom';
import type { SessionUser, Permission } from '@shawwir/contracts';
import { api, ApiError, setSessionExpiredHandler } from './api';

/**
 * AUTH-3..AUTH-9 on the client side.
 *
 * The session itself lives in an httpOnly cookie the JavaScript cannot read, so
 * "am I signed in?" is answered by asking the server once on boot. There is no
 * token in localStorage to steal.
 */

interface AuthState {
  user: SessionUser | null;
  status: 'loading' | 'authenticated' | 'anonymous';
  login: (phone: string, password: string, rememberMe: boolean) => Promise<void>;
  logout: () => Promise<void>;
  /** AUTH-9: drives menus, row actions and bulk actions. */
  can: (permission: Permission) => boolean;
  sessionExpired: boolean;
  dismissSessionExpired: () => void;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<SessionUser | null>(null);
  const [status, setStatus] = useState<AuthState['status']>('loading');
  const [sessionExpired, setSessionExpired] = useState(false);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();

  // Ask the server who we are, once, on boot.
  useEffect(() => {
    let cancelled = false;
    api
      .get<{ data: SessionUser }>('/auth/me')
      .then((res) => {
        if (cancelled) return;
        setUser(res.data);
        setStatus('authenticated');
      })
      .catch(() => {
        if (cancelled) return;
        setUser(null);
        setStatus('anonymous');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  /**
   * AUTH-6: any 401 from anywhere invalidates the session and bounces to login
   * with a clear message -- never a generic "Unable to fetch X" toast.
   */
  useEffect(() => {
    setSessionExpiredHandler(() => {
      setUser((current) => {
        if (current) setSessionExpired(true);
        return null;
      });
      setStatus('anonymous');
      queryClient.clear();

      const here = `${location.pathname}${location.search}`;
      if (!here.startsWith('/login')) {
        navigate(`/login?next=${encodeURIComponent(here)}`, { replace: true });
      }
    });
    return () => setSessionExpiredHandler(null);
  }, [navigate, queryClient, location.pathname, location.search]);

  const value = useMemo<AuthState>(
    () => ({
      user,
      status,
      sessionExpired,
      dismissSessionExpired: () => setSessionExpired(false),

      login: async (phone, password, rememberMe) => {
        const res = await api.post<{ data: SessionUser }>(
          '/auth/login',
          { phone, password, rememberMe },
          // A failed login is not an expired session; do not bounce.
          { skipAuthRedirect: true },
        );
        setUser(res.data);
        setStatus('authenticated');
        setSessionExpired(false);
        // Never show the previous user's cached data to the next one.
        queryClient.clear();
      },

      logout: async () => {
        try {
          await api.post('/auth/logout');
        } catch (err) {
          // A failed logout call must still clear the client. Worst case the
          // cookie outlives the click, and the next request 401s.
          if (!(err instanceof ApiError)) throw err;
        }
        setUser(null);
        setStatus('anonymous');
        queryClient.clear();
        navigate('/login', { replace: true });
      },

      can: (permission) => user?.permissions.includes(permission) ?? false,
    }),
    [user, status, sessionExpired, queryClient, navigate],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}

/** Convenience for the very common "can I do X?" check in a component. */
export function useCan(permission: Permission): boolean {
  return useAuth().can(permission);
}
